import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../../../../theme";
import { resolvePalette } from "../../../../../data/palettes.js";

export default function VisionSwatch({ schemeKey, scheme: rawScheme, active, onSelect }) {
  const t = useTheme();
  const scheme = resolvePalette(rawScheme, t.mode);
  const [hovered, setHovered] = useState(false);
  const show = hovered;
  const wrapRef = useRef(null);
  const [shiftX, setShiftX] = useState(0);
  const TOOLTIP_WIDTH = 210;
  const VIEWPORT_PADDING = 8;
  const ARROW_BASE_LEFT = 18;

  // Clamp the tooltip into the viewport when the swatch sits near the right
  // edge (rightmost row member on mobile). Shift left by the overflow, then
  // compensate the arrow so it still points down at the swatch.
  useEffect(() => {
    if (!hovered || !wrapRef.current || typeof window === "undefined") return;
    const rect = wrapRef.current.getBoundingClientRect();
    const overflowRight =
      rect.left + TOOLTIP_WIDTH - (window.innerWidth - VIEWPORT_PADDING);
    const overflowLeft = VIEWPORT_PADDING - rect.left;
    let next = 0;
    if (overflowRight > 0) next = -overflowRight;
    if (overflowLeft > 0 && overflowLeft > next) next = Math.min(0, overflowLeft);
    setShiftX(next);
  }, [hovered]);

  // Keep the arrow under the swatch even when the tooltip is shifted, but
  // never let it slide past the tooltip's own bounds.
  const arrowLeft = Math.max(
    9,
    Math.min(TOOLTIP_WIDTH - 18, ARROW_BASE_LEFT - shiftX)
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={onSelect}
        style={{
          width: 44, height: 44, borderRadius: 10, padding: 0,
          background: active ? `${scheme.pivot}18` : hovered ? t.surfaceHover : t.surface,
          border: `1.5px solid ${active ? `${scheme.pivot}66` : hovered ? `${scheme.pivot}33` : t.border}`,
          cursor: "pointer", transition: "all 0.2s ease",
          display: "flex", alignItems: "center", justifyContent: "center",
          outline: "none",
          boxShadow: active ? `0 0 14px rgba(${scheme.glow},0.15)` : "none",
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: "50%",
          background: scheme.pivot,
          boxShadow: active ? `0 0 8px rgba(${scheme.glow},0.4)` : "none",
          transition: "box-shadow 0.2s ease",
          transform: hovered && !active ? "scale(1.15)" : "scale(1)",
        }} />
      </button>
      {/* Tooltip — positioned above, clamped to viewport */}
      <div style={{
        position: "absolute",
        bottom: "calc(100% + 12px)",
        left: 0,
        transform: `translateX(${shiftX}px) translateY(${show ? 0 : 4}px) scale(${show ? 1 : 0.92})`,
        transformOrigin: `${arrowLeft}px bottom`,
        opacity: show ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity 0.2s ease, transform 0.2s ease",
        background: t.tooltipBg,
        border: `1px solid ${active ? `${scheme.pivot}44` : t.borderStrong}`,
        borderRadius: 10,
        padding: "12px 14px",
        width: TOOLTIP_WIDTH,
        zIndex: 999,
        boxShadow: "0 8px 32px " + t.shadowStrong,
      }}>
        <div style={{
          position: "absolute",
          bottom: -5,
          left: arrowLeft,
          transform: "rotate(45deg)",
          width: 9,
          height: 9,
          background: t.tooltipBg,
          borderRight: `1px solid ${active ? `${scheme.pivot}44` : t.borderStrong}`,
          borderBottom: `1px solid ${active ? `${scheme.pivot}44` : t.borderStrong}`,
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: scheme.pivot, boxShadow: `0 0 6px rgba(${scheme.glow},0.3)` }} />
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: scheme.pivot, fontWeight: 500, letterSpacing: "0.04em" }}>{scheme.name}</span>
        </div>
        <p style={{ margin: 0, fontFamily: "'DM Mono', monospace", fontSize: 9.5, color: t.textLow, lineHeight: 1.55, letterSpacing: "0.01em" }}>{scheme.desc}</p>
        <div style={{ marginTop: 10, padding: "7px 0", borderTop: "1px solid " + t.borderSubtle, fontFamily: "'DM Mono', monospace", fontSize: 18, textAlign: "center", letterSpacing: "0.04em" }}>
          <span style={{ color: t.textMid }}>Br</span>
          <span style={{ color: scheme.pivot, fontWeight: 600, textShadow: `0 0 10px rgba(${scheme.glow},0.3)` }}>o</span>
          <span style={{ color: t.textMid }}>wn</span>
        </div>
      </div>
    </div>
  );
}
